import { axiosDelete, axiosGet, axiosPatch, axiosPost } from '@/api/handlers';
import { useMutation, useQuery } from '@/api/hooks';
import { useInvalidateQuery } from '@/hooks';
import { z } from 'zod';
import {
  ExpenditureBudgetOrganizationFormSchema,
  ExpenditureBudgetOrganizationsModel,
} from '../models';

const ExpenditureBudgetOrganizationsModelElement =
  ExpenditureBudgetOrganizationsModel.element;

export const useExpenditureBudgetOrganizations = <
  T = Array<z.infer<typeof ExpenditureBudgetOrganizationsModelElement>>,
>({
  select,
  params,
}: {
  params?: { showMigrated?: true };
  select?: (data: z.infer<typeof ExpenditureBudgetOrganizationsModel>) => T;
}) => {
  const { showMigrated, ...restParams } = params ?? {};
  return useQuery({
    queryKey: ['expenditureBudgetOrganizations'],
    queryFn: async () => {
      const data = await axiosGet('/api/expenditure-budget-organizations/', {
        ...restParams,
        ...(!showMigrated && { isMigrated: false }),
      });
      return ExpenditureBudgetOrganizationsModel.parse(data);
    },
    select,
  });
};

export const useExpenditureBudgetOrganizationOptions = (props?: {
  params?: { showMigrated?: true };
}) => {
  const { params } = props ?? {};
  return useExpenditureBudgetOrganizations({
    params,
    select: (data) =>
      data.map((d) => ({
        value: d.id,
        label: d.name,
      })),
  });
};

type FormValues = z.infer<typeof ExpenditureBudgetOrganizationFormSchema>;

export const useCreateExpenditureBudgetOrganization = () => {
  return useMutation({
    mutationFn: async (values: FormValues) => {
      const { period, ...payload } =
        ExpenditureBudgetOrganizationFormSchema.parse(values);
      return await axiosPost('/api/expenditure-budget-organizations/', {
        ...payload,
        startYear: period?.[0]?.year() ?? null,
        endYear: period?.[1]?.year() ?? null,
      });
    },
  });
};

export const useUpdateExpenditureBudgetOrganization = () => {
  return useMutation({
    mutationFn: async (values: FormValues & { id: string }) => {
      const { id, period, ...payload } =
        ExpenditureBudgetOrganizationFormSchema.extend({
          id: z.string().uuid(),
        }).parse(values);
      return await axiosPatch(`/api/expenditure-budget-organizations/${id}/`, {
        ...payload,
        startYear: period?.[0]?.year() ?? null,
        endYear: period?.[1]?.year() ?? null,
      });
    },
  });
};

export const useDeleteExpenditureBudgetOrganization = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await axiosDelete(
        `/api/expenditure-budget-organizations/${id}/`,
      );
      await invalidate(['expenditureBudgetOrganizations']);
      return res;
    },
  });
};
